import {type PressEvent} from '@react-types/shared';
import _ from 'lodash';
import {type RefObject, useCallback, useEffect, useMemo, useState} from 'react';
import {useStore} from 'react-redux';

import {useMemoizedSelector} from '../../../state/hooks';
import type * as CustomRunColorsTypes from '../../../state/views/customRunColors/types';
import type * as CustomRunNamesTypes from '../../../state/views/customRunNames/types';
import * as GroupSelectionsActions from '../../../state/views/groupSelections/actions';
import type * as GroupSelectionsTypes from '../../../state/views/groupSelections/types';
import * as ViewHooks from '../../../state/views/hooks';
import {type Ref} from '../../../state/views/runSet/types';
import type * as SemanticLegendTypes from '../../../state/views/semanticLegend/types';
import * as TempSelectionsActions from '../../../state/views/tempSelections/actions';
import * as TempSelectionsActionsInternal from '../../../state/views/tempSelections/actionsInternal';
import type * as TempSelectionsTypes from '../../../state/views/tempSelections/types';
import type {RootState} from '../../../types/redux';
import {EMPTY_CONFIG as EMPTY_TABLE_SETTINGS} from '../../../util/runfeed';
import * as Run from '../../../util/runs';
import type * as RunTypes from '../../../util/runTypes';
import * as SM from '../../../util/selectionmanager';
import {
  getChildRowAddress,
  makeRowAddressComponent,
  type WBTableRowWithRunSetInfo,
} from '../../../util/tablecols';
import {type TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {type JobInfo} from '../../Launch/utilTypes';
import {getRangeRows, type RangeRow} from '../rangeSelection';
import {
  makeGroupSelectionsSelector,
  makeRunSetSelector,
  makeTempSelectionsSelector,
} from '../runSetSelectors';
import {isCrossProjectRun} from '../util';
import {useRunColor} from './useRunColor';

/** the row a shift+click range starts from */
export type RangeAnchor = {
  /** the row's __address__, which is what unmount clears by */
  address: string;
  path: SM.SelectionPath;
};

export interface RunSelectorRowHeaderProps {
  className?: string;
  customRunColorsRef: CustomRunColorsTypes.Ref;
  customRunNamesRef: CustomRunNamesTypes.Ref;
  disableRunLink?: boolean;
  entityName: string;
  groupSelectionsRef: GroupSelectionsTypes.Ref;
  isGroup?: boolean; // false if row is individual run, true if it's a group of runs
  isGroupExpanded: boolean;
  isHoveringRow: boolean;
  isLaunchActive?: boolean;
  isLaunchPublicQueueActive?: boolean;
  isSingleMode?: boolean;
  isTableCollapsed?: boolean;
  loadingChildren?: boolean;
  onGroupButtonClick?(): void;
  onSelectRunName?: (runId: string) => void;
  onTempCheckboxClick(): void;
  projectName: string;
  recursionDepth: number;
  row: WBTableRowWithRunSetInfo;
  runSetRef: Ref;
  selectedCount: number;
  selectedRunName?: string;
  semanticLegendRef: SemanticLegendTypes.Ref;
  setLaunchDrawerRow(run: string, jobInfo: JobInfo): void;
  showArtifactCounts?: boolean;
  showLogCounts?: boolean;
  stopRun(id: string): Promise<any>; // from withMutations
  tempSelectionsRef: TempSelectionsTypes.Ref;
  truncationType: TruncationType;
}

export type RangeAnchorRef = RefObject<RangeAnchor | null>;

// a ref inside props makes the compiler treat every props read as a ref read,
// so the anchor ref is its own argument
export function useRunSelectorRowHeaderProps(
  props: RunSelectorRowHeaderProps,
  anchorRef: RangeAnchorRef
) {
  // Read the parts via reselect-based selectors so derived computations can
  // declare both the part state AND the row as deps. The previous
  // usePartMapped approach only re-invoked its mapper when the part itself
  // changed, which returned a stale result when props.row updated with
  // newly-loaded summary data — the visibility checkbox would not
  // recalculate once a brushed PC axis's summary metric finally landed on
  // the run.
  const groupSelections = useMemoizedSelector(
    makeGroupSelectionsSelector,
    props.groupSelectionsRef
  );
  const tempSelections = useMemoizedSelector(
    makeTempSelectionsSelector,
    props.tempSelectionsRef
  );

  const grouping = groupSelections.grouping;
  const isGroupingEnabled = grouping.length > 0;
  const expandedRowAddresses = groupSelections.expandedRowAddresses;

  const visCheckboxState = useMemo(
    () =>
      SM.getCheckedState(groupSelections, props.row, props.recursionDepth + 1),
    [groupSelections, props.row, props.recursionDepth]
  );

  const runIsInBounds = useMemo(
    () =>
      SM.runIsInBounds(groupSelections, props.row, props.recursionDepth + 1),
    [groupSelections, props.row, props.recursionDepth]
  );

  const tempCheckboxState = useMemo(() => {
    const tempGroupSelections = {
      grouping,
      selections: tempSelections,
      expandedRowAddresses,
    };
    return SM.getCheckedState(
      tempGroupSelections,
      props.row,
      props.recursionDepth + 1
    );
  }, [
    grouping,
    tempSelections,
    expandedRowAddresses,
    props.row,
    props.recursionDepth,
  ]);

  const toggleSelection = ViewHooks.useViewAction(
    props.groupSelectionsRef,
    GroupSelectionsActions.toggleSelection
  );

  const tempSelectToggle = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectToggle,
    props.tempSelectionsRef,
    props.runSetRef,
    props.row,
    props.recursionDepth + 1
  );
  // setTempSelections is undoable, so a range is one undo step
  const setTempSelections = ViewHooks.useViewAction(
    props.tempSelectionsRef,
    TempSelectionsActionsInternal.setTempSelections
  );

  const store = useStore<RootState>();

  const runSetId = props.runSetRef.id;
  const rowAddress = props.row.__address__;
  const rowDepth = props.recursionDepth + 1;

  // effect, not render: a row that leaves the screen (page change, collapse,
  // regroup) can't anchor a range. it only clears its own anchor, so another
  // row's unmount can't drop it
  useEffect(() => {
    return () => {
      if (anchorRef.current?.address === rowAddress) {
        anchorRef.current = null;
      }
    };
  }, [anchorRef, rowAddress]);

  const handleTempCheckboxPress = (event: PressEvent) => {
    // read from the store at press time: RunSelectorRowHeaderInner's memo
    // ignores functions, so this handler can be several renders old
    const state = store.getState();
    const {expandedRowAddresses, grouping} = makeGroupSelectionsSelector(
      props.groupSelectionsRef
    )(state);
    const anchor = anchorRef.current;
    const rowPath = SM.getSelectionPath(grouping, props.row, rowDepth);
    anchorRef.current = {address: rowAddress, path: rowPath};

    const isRangePress =
      event.shiftKey && anchor != null && anchor.address !== rowAddress;
    if (!isRangePress) {
      tempSelectToggle();
      return;
    }

    // the same rows and addresses WBTable renders, from getRows and WBTableRow
    const runSet = makeRunSetSelector(props.runSetRef)(state);
    const topRuns = state.runSelectorRuns[runSetId];
    const toTopRow = (run: RunTypes.Run): RangeRow => ({
      ...run,
      __address__: makeRowAddressComponent(run.id, runSet.id),
    });
    const workspaceProject = {
      entityName: props.entityName,
      name: props.projectName,
    };
    const rangeRows = getRangeRows({
      anchorPath: anchor.path,
      getChildRows: address =>
        state.runSelectorRuns[address]?.runs.map(run => ({
          ...run,
          __address__: getChildRowAddress(address, run.id),
        })) ?? null,
      grouping,
      // the same fallback WBTable pages with, so the page matches the screen
      pageSize: runSet.runFeed?.pageSize || EMPTY_TABLE_SETTINGS.pageSize,
      // a cross-project run's checkbox is disabled, so a range skips it too
      pinnedRows: (topRuns?.pinnedRuns ?? [])
        .filter(
          run => !isCrossProjectRun(run.project ?? undefined, workspaceProject)
        )
        .map(toTopRow),
      rows: (topRuns?.runs ?? []).map(toTopRow),
      targetPath: rowPath,
    });
    if (rangeRows == null) {
      tempSelectToggle();
      return;
    }

    const tempGroupSelections = {
      expandedRowAddresses,
      grouping,
      selections: makeTempSelectionsSelector(props.tempSelectionsRef)(state),
    };
    // the range takes the anchor's state, and a partial anchor checks
    const [anchorRow] = rangeRows;
    const anchorState = SM.getCheckedState(
      tempGroupSelections,
      anchorRow.run,
      anchorRow.depth
    );
    setTempSelections(
      SM.selectRange(
        tempGroupSelections,
        rangeRows,
        anchorState !== 'unchecked'
      ).selections
    );
  };

  const uniqueId = Run.uniqueId(props.row, grouping);
  const groupCounts = props.row.groupCounts ?? [];
  const childGroupCount =
    groupCounts.length >= 2 ? groupCounts[groupCounts.length - 2] : null;
  const isParentGroup = Boolean(props.isGroup && childGroupCount != null);
  // a group color cascades to every run under it; a run row colors only itself
  const affectedRunCount = props.isGroup
    ? (groupCounts[groupCounts.length - 1] ?? 0)
    : 1;
  const {
    clearCustomRunColor,
    customRunColor,
    customRunColorPreviewColors,
    customRunColorSource,
    hasCustomRunColorDescendantColors,
    hasParentInheritedColor,
    isCustomRunColorClearable,
    setCustomRunColor,
  } = useRunColor({
    customRunColorsRef: props.customRunColorsRef,
    entityName: props.entityName,
    grouping,
    isGroup: props.isGroup,
    projectName: props.projectName,
    recursionDepth: props.recursionDepth,
    row: props.row,
    runSetRef: props.runSetRef,
    semanticLegendRef: props.semanticLegendRef,
  });

  const customRunName = ViewHooks.usePartMapped(
    props.customRunNamesRef,
    customRunNames =>
      Run.getCustomRunName(
        props.row.name,
        Boolean(props.isGroup),
        customRunNames
      )
  );

  const groupedBy = grouping[props.recursionDepth];
  const isRunGroup = _.isEqual(groupedBy, {section: 'run', name: 'group'});

  const [stopModalOpen, setStopModalOpen] = useState(false);
  const openStopModal = useCallback(
    () => setStopModalOpen(true),
    [setStopModalOpen]
  );
  const closeStopModal = useCallback(
    () => setStopModalOpen(false),
    [setStopModalOpen]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const openDeleteModal = useCallback(
    () => setDeleteModalOpen(true),
    [setDeleteModalOpen]
  );
  const closeDeleteModal = useCallback(
    () => setDeleteModalOpen(false),
    [setDeleteModalOpen]
  );

  const {id} = props.row;
  const {stopRun} = props;
  const stopRunCurried = useCallback(() => stopRun(id), [stopRun, id]);

  return {
    closeDeleteModal,
    closeStopModal,
    clearCustomRunColor,
    customRunColor,
    affectedRunCount,
    isCustomRunColorClearable,
    customRunColorSource,
    customRunColorPreviewColors,
    hasCustomRunColorDescendantColors,
    hasParentInheritedColor,
    isGroupingEnabled,
    isParentGroup,
    customRunName,
    setDeleteModalOpen,
    deleteModalOpen,
    groupedBy,
    isRunGroup,
    openDeleteModal,
    openStopModal,
    runIsInBounds,
    setCustomRunColor,
    setLaunchDrawerRow: props.setLaunchDrawerRow,
    stopModalOpen,
    stopRun: stopRunCurried,
    onTempCheckboxPress: handleTempCheckboxPress,
    tempCheckboxState,
    toggleSelection,
    uniqueId,
    visCheckboxState,
  };
}
